<template>
  <div v-if="research">
    <div class="d-flex justify-content-between">
      <h2>
        {{ research.title }} <small class="text-muted">{{ research.organisation.name }}</small>
      </h2>
      <div class="d-flex">
        <div class="mr-2">
          <a
            class="btn btn-secondary btn-sm"
            :href="`/res/${research.uuid}/?test=true`"
            target="_blank"
          >
            <i class="fa fa-check-double mr-2"></i>
            {{ $t('word.test') }}
          </a>
        </div>
        <div class="mr-2">
          <v-button
            icon="eye"
            text="word.validate"
            @click="validateResearch"
            style-type="secondary"
            size="sm"
            :loader="validateLoader"
          />
        </div>
        <div class="mr-2" v-if="research.status === 'draft' && isLocked">
          <v-button
            icon="lock-open"
            text="word.unlock"
            @click="unlockResearch"
            size="sm"
          />
        </div>
        <div class="mr-2">
          <v-button
            v-if="research.status === 'draft'"
            icon="edit"
            text="word.edit"
            @click="goToResearch"
            style-type="secondary"
            size="sm"
          />
          <v-button
            v-else
            icon="chart-bar"
            text="word.view"
            @click="goToResearch"
            style-type="secondary"
            size="sm"
          />
        </div>
        <div class="mr-2">
          <v-button
            icon="copy"
            text="word.copy"
            size="sm"
            style-type="secondary"
            type="button"
            @click="showCopyModal = true"
          />
        </div>
        <div class="mr-2">
          <v-button
            v-if="!['draft', 'planned'].includes(research.status)"
            icon="download"
            text="word.export"
            size="sm"
            style-type="secondary"
            type="button"
            :loader="exportLoader"
            @click="() => exportFile('csv')"
          />
        </div>
        <div>
          <a
            class="btn btn-secondary btn-sm"
            target="_blank"
            :href="getBackendUrl(`admin/core/research/${research.id}/`)"
          >
            <i class="fa fa-external-link mr-2"></i>
            Open in Django Admin
          </a>
        </div>
      </div>
    </div>

    <div v-if="validationErrors.length > 0">
      <div
        class="alert alert-danger p-1" role="alert"
        v-for="(error, index) in validationErrors" :key="index"
      >
        <i class="fa fa-exclamation-triangle mr-2"></i>
        {{ error.message }}
        <a
          v-if="error.research_item_id"
          class="btn btn-link btn-xs"
          :href="`/admin/core/researchitem/${ error.research_item_id }/`"
          target="_blank"
        >
          Open research item: {{ error.research_item_id }}
        </a>
      </div>
    </div>

    <div class="row">
      <div class="col-md-4">
        <v-research-general-info :research="research"/>
      </div>
      <div class="col-md-8">
        <v-research-structure-view
          :research="research"
          :research-items="researchItems || []"
        />
      </div>
    </div>

    <v-research-copy-modal
      v-if="showCopyModal"
      :research-uuid="research.uuid"
      @hide-modal="showCopyModal = false"
    />
  </div>
</template>

<script>
import Button from '@/common/components/Button.vue';
import ResearchCopyModal from '@/admin/components/ResearchCopyModal.vue';
import ResearchGeneralInfo from '@/common/components/research/ResearchGeneralInfo.vue';
import ResearchStructureView from '@/common/components/research/ResearchStructureView.vue';
import {
  fetchResearchType,
  fetchResearchItems,
  validateResearch,
  createResearchExport,
} from '@/organisation/api';
import { getBackendUrl } from '@/common/utils/api';
import {
  createOrganisationMembership,
  fetchResearch,
  unlockResearch,
} from '@/admin/api';
import { notify } from '@/common/utils/notify';

export default {
  name: 'ResearchDetail',
  components: {
    'v-research-general-info': ResearchGeneralInfo,
    'v-button': Button,
    'v-research-copy-modal': ResearchCopyModal,
    'v-research-structure-view': ResearchStructureView,
  },
  data() {
    return {
      research: null,
      researchType: null,
      researchItems: [],
      showCopyModal: false,
      validateLoader: { loading: false },
      validationErrors: [],
      exportLoader: { loading: false },
    };
  },
  computed: {
    isLocked() {
      return this.researchItems.some((item) => item.is_locked);
    },
  },
  methods: {
    getBackendUrl,
    async exportFile(fileType) {
      this.exportLoader.loading = true;
      const response = await createResearchExport(this.research.uuid, fileType);
      document.location.href = `/api/org/research-exports/${response.data.uuid}/download/`;
      this.exportLoader.loading = false;
    },
    async fetchResearch() {
      const response = await fetchResearch(this.$route.params.uuid);
      if (response.status === 200) {
        this.research = response.data;
      }
    },
    async fetchResearchType() {
      const response = await fetchResearchType(
        this.research.research_type.slug, this.research.organisation.slug,
      );
      if (response.status !== 200) {
        this.pageNotFound();
        return;
      }
      this.researchType = response.data;
    },
    async fetch() {
      await this.fetchResearch();
      await this.fetchResearchItems();
      await this.fetchResearchType();
    },
    async fetchResearchItems() {
      const response = await fetchResearchItems(this.$route.params.uuid);
      this.researchItems = response.data;
    },
    async goToResearch() {
      const response = await createOrganisationMembership(this.research.organisation.slug);
      if (response.status === 201) {
        await this.$router.push({
          name: 'organisation-research-dashboard',
          params: { uuid: this.research.uuid },
        });
      } else {
        alert('Something went wrong, please try again.');
      }
    },
    async unlockResearch() {
      // eslint-disable-next-line no-restricted-globals
      const answer = confirm('Are you sure you want to unlock this research?');
      if (answer) {
        const response = await unlockResearch(this.$route.params.uuid);
        if (response.status === 200) {
          document.location.reload();
        } else {
          alert('Something went wrong, please try again.');
        }
      }
    },
    async validateResearch() {
      this.validateLoader.loading = true;
      const response = await validateResearch(this.$route.params.uuid);
      if (response.status === 200) {
        notify(
          this.$t('phrase.validation_success'),
          this.$t('sentence.research_validation_success'),
          'success',
        );
      } else if (response.status === 400) {
        notify(
          this.$t('phrase.validation_failed'),
          this.$t('sentence.research_validation_failed'),
          'error',
        );
        this.validationErrors = response.data;
      } else {
        alert('Something went wrong, please try again.');
      }
      this.validateLoader.loading = false;
    },
  },
  created() {
    this.fetch();
  },
};
</script>
